import { useEffect } from 'react';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import GDPREnforcement from '../utils/gdprHelpers';
import config from '../config';

/**
 * Hook for GDPR-compliant analytics integration
 * Automatically enables/disables Google Analytics based on user consent
 */
export const useAnalytics = () => {
  const { hasConsent, consent } = useCookieConsent();

  // Check if analytics is enabled
  const analyticsEnabled = hasConsent('analytics');

  // Effect to handle analytics loading/unloading based on consent
  useEffect(() => {
    // Enforce analytics consent
    GDPREnforcement.enforceAnalyticsConsent(analyticsEnabled);
  }, [analyticsEnabled, consent]);

  /**
   * Track page view (only if analytics consent given)
   */
  const trackPageView = (page: string, title?: string) => {
    if (!analyticsEnabled) {
      return;
    }

    if (window.gtag) {
      window.gtag('config', config.analytics.googleAnalytics.measurementId, {
        page_title: title,
        page_location: window.location.href
      });
      
      // Also send explicit page_view event
      window.gtag('event', 'page_view', {
        page_title: title,
        page_location: window.location.href,
        page_path: page
      });
    }
  };

  /**
   * Track custom event (only if analytics consent given)
   */
  const trackEvent = (action: string, category: string, label?: string, value?: number) => {
    if (!analyticsEnabled) {
      return;
    }

    if (window.gtag) {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value
      });
    }
  };

  /**
   * Track user interaction (only if analytics consent given)
   */
  const trackUserInteraction = (element: string, action: string, additional?: object) => {
    if (!analyticsEnabled) return;

    trackEvent(action, 'User Interaction', element);
  };

  /**
   * Track conversion event (only if analytics consent given)
   */
  const trackConversion = (type: 'signup' | 'purchase' | 'download', value?: number, details?: object) => {
    if (!analyticsEnabled) return;

    trackEvent(`conversion_${type}`, 'Conversions', type, value);
  };

  /**
   * Get analytics status for debugging
   */
  const getAnalyticsStatus = () => {
    return {
      consentGiven: analyticsEnabled,
      gaLoaded: !!window.gtag,
      cookieConsent: consent,
      measurementId: config.analytics.googleAnalytics.measurementId,
      analyticsEnabled: config.analytics.googleAnalytics.enabled,
      gaScript: !!document.querySelector('[data-gtag-loaded]')
    };
  };

  /**
   * Manually trigger consent check and analytics reload
   */
  const reloadAnalytics = () => {
    GDPREnforcement.enforceAnalyticsConsent(analyticsEnabled);
  };

  return {
    analyticsEnabled,
    trackPageView,
    trackEvent,
    trackUserInteraction,
    trackConversion,
    getAnalyticsStatus,
    reloadAnalytics
  };
};

export default useAnalytics;